import EntityModel from '@/common/models/entity-model';
import timezone from '@/common/timezone';
import {DateTimeFormats} from '@/common/datetime-formats';
import _ from '@/common/helpers';
import CriteriaCondition from '@/components/query/spec/criteria-condition';

export class TimelineMessageDataTableDataModel extends EntityModel {
    hydrate({
        attribute = null,
        value = null,
        newValue = null,
        previousValue = null,
        userFullName = null,
        userId = null,
    }) {
        this.attribute = attribute;
        this.value = value;
        this.newValue = newValue;
        this.previousValue = previousValue;
        this.userFullName = userFullName;
        this.userId = userId;
        if (this.attribute && this.value) {
            // check for datetime value, parse into correct timezone
            // TODO update test after dates are changed
            if (/time/i.test(this.attribute) && /at/i.test(this.value)) {
                const pattern = DateTimeFormats.longDateTime;
                this.value = timezone
                    .readUTCFormat(this.value, pattern)
                    .format(pattern);
            }
        }
        const subValue = value || newValue;
        if (subValue) {
            // Try to transform subValue into its model
            try {
                if (attribute === 'restriction' && subValue.conditions) {
                    // An object containing a condition(s) must be transformed
                    // using its model since rendering is more complex.
                    this.isRestriction = true;
                    this.value = new CriteriaCondition(subValue);
                    this.newValue = this.value;
                    if (previousValue) {
                        this.previousValue = new CriteriaCondition(previousValue);
                    }
                } else {
                    this.isComplex = TimelineMessageDataTableDataModel.isComplex(subValue);
                    if (this.isComplex) {
                        this.isComplexArray = TimelineMessageDataTableDataModel
                            .isComplexArray(subValue);
                        this.subValues = TimelineMessageDataTableDataModel.getSubValues(this);
                    }
                }
            } catch (error) {
                // subValues failed to transform.
            }
        }
    }

    /**
     * Extract sub values contained in value or newValue
     * @param {Object} entity - a TimelineMessageDataTableDataModel object
     * @return {Object | Array} type TimelineMessageDataTableDataModel
     * */
    static getSubValues({
        attribute, value, newValue, previousValue, isComplexArray,
    }) {
        const subValue = value || newValue;
        if (isComplexArray) {
            if (subValue.length === 1) {
                return this.ObjectToSubValues(subValue[0], previousValue && previousValue[0]);
            }
            return this.ArrayToSubValues(subValue, previousValue, attribute);
        }
        return this.ObjectToSubValues(subValue, previousValue);
    }

    /**
     * Determine if a value is complex
     * @param {Object | Array} value
     * @return {Boolean}
     * */
    static isComplex(value) {
        return TimelineMessageDataTableDataModel.isComplexArray(value) || _.isObject(value);
    }

    /**
     * Determine if a value is a complex array
     * @param {Array} value
     * @return {Boolean}
     * */
    static isComplexArray(value) {
        return Array.isArray(value) && (Array.isArray(value[0]) || _.isObject(value[0]));
    }

    /**
     * Transform a value Array into an Array of TimelineMessageDataTableDataModel
     * @param {Array} value
     * @param {Array?} previousValue
     * @param {String} attribute
     * @return {Array}
     * */
    static ArrayToSubValues(value, previousValue, attribute) {
        return value.map(
            (subValue, index) => new TimelineMessageDataTableDataModel(
                {
                    attribute: `${attribute} element ${index + 1}`,
                    value: subValue,
                    newValue: subValue,
                    previousValue: previousValue
                        && previousValue.length
                        && previousValue[index],
                },
            ),
        );
    }

    /**
     * Transform a value Object into an Array of TimelineMessageDataTableDataModel
     * @param {Array} value
     * @param {Array?} previousValue
     * @return {Array}
     * */
    static ObjectToSubValues(value, previousValue) {
        return Object.keys(value).map(key => new TimelineMessageDataTableDataModel(
            {
                attribute: key,
                value: value[key],
                newValue: value[key],
                previousValue: previousValue
                    && previousValue[key],
            },
        ));
    }
}

export default class TimelineMessageDataTableModel extends EntityModel {
    hydrate({
        type = null,
        title = null,
        footer = null,
        data = [],
    } = {}) {
        this.type = type;
        this.title = title;
        this.footer = footer;
        this.data = data.map(v => new TimelineMessageDataTableDataModel(v));
    }

    static Types = {
        oneColumn: 'one-column',
        twoColumns: 'two-columns',
        threeColumns: 'three-columns',
    };

    static AttributeNameDelimiter = '.'

    get isThreeColumns() {
        return this.type === TimelineMessageDataTableModel.Types.threeColumns;
    }

    get isOneColumn() {
        return this.type === TimelineMessageDataTableModel.Types.oneColumn;
    }

    /**
     * Return the value of an attribute based on
     * its exact name within the table.
     * @param attribute
     * @returns {*}
     */
    getDataAttribute(attribute) {
        const match = this.data.find(v => v.attribute === attribute);
        if (match) {
            return match.value;
        }
        return null;
    }
}
